// ✅ MIGRATED TO V2 DESIGN SYSTEM - Volvo/Teal Theme
import React, { useState, useEffect } from 'react';
import { CreditCard, CheckCircle, AlertCircle, ExternalLink, Loader2 } from 'lucide-react';
import { notify } from '@/stores/notificationStore';
import { stripeConnectAPI } from '@/utils/api';
import { useAuthStore } from '@/stores/authStore';
import { logger } from '@/utils/logger';
import { Button, Spinner } from '@/components/ui-v2';

interface StripeConnectStatus {
  exists: boolean;
  enabled: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  onboardingRequired: boolean;
}

export const StripeConnectSettings: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const [status, setStatus] = useState<StripeConnectStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [onboarding, setOnboarding] = useState(false);

  // V2 Card classes - avec break-inside-avoid pour le layout masonry
  const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
    border border-white/30 p-6 hover:bg-white/70 hover:shadow-2xl
    transition-all duration-300 break-inside-avoid mb-6`;

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const data = await stripeConnectAPI.getStatus();
      if (data.success) {
        setStatus(data.data);
      }
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Impossible de récupérer le statut Stripe Connect', 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async () => {
    try {
      setOnboarding(true);
      const data = await stripeConnectAPI.createAccount({
        name: user?.fullName || user?.email || '',
        email: user?.email || '',
        country: 'FR',
        businessType: 'company',
      });
      if (data.success) {
        notify.success('Compte Stripe Connect créé !', 'Succès');
        await handleStartOnboarding();
      }
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors de la création du compte', 'Erreur');
      setOnboarding(false);
    }
  };

  const handleStartOnboarding = async () => {
    try {
      setOnboarding(true);
      const data = await stripeConnectAPI.getOnboardingLink();
      if (data.success && data.data.url) {
        window.location.href = data.data.url;
      }
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors du démarrage de l\'onboarding', 'Erreur');
      setOnboarding(false);
    }
  };

  const handleOpenDashboard = async () => {
    try {
      const data = await stripeConnectAPI.getDashboardLink();
      if (data.success && data.data.url) {
        window.open(data.data.url, '_blank');
      }
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors de l\'ouverture du dashboard', 'Erreur');
    }
  };

  if (loading) {
    return (
      <div className={cardClasses}>
        <div className="flex items-center justify-center py-8">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div className={cardClasses}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-teal-100 rounded-xl">
          <CreditCard className="w-5 h-5 text-teal-600" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900">Paiements Stripe Connect</h2>
      </div>

      <div className="space-y-4">
        {/* Explication */}
        <div className="bg-teal-50/50 backdrop-blur-sm border border-teal-200/50 rounded-2xl p-4 hover:bg-teal-50/70 transition-all duration-300">
          <h3 className="font-semibold text-teal-900 mb-2">
            Comment ça marche ?
          </h3>
          <p className="text-sm text-teal-800">
            Stripe Connect vous permet de recevoir directement les paiements de vos factures sur votre propre compte Stripe.
            La plateforme prélève une petite commission de 2% sur chaque paiement.
          </p>
        </div>

        {/* Statut - Configuration requise */}
        {status && !status.exists && (
          <div className="bg-orange-50/50 backdrop-blur-sm border border-orange-200/50 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-orange-100 rounded-xl">
                <AlertCircle className="w-5 h-5 text-orange-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-orange-900 mb-1">
                  Configuration requise
                </h3>
                <p className="text-sm text-orange-800 mb-4">
                  Pour recevoir les paiements de vos factures directement, vous devez configurer votre compte Stripe Connect.
                </p>
                <Button
                  onClick={handleCreateAccount}
                  disabled={onboarding}
                  icon={onboarding ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                >
                  {onboarding ? 'Configuration en cours...' : 'Configurer Stripe Connect'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Statut - Onboarding en attente */}
        {status && status.exists && !status.enabled && (
          <div className="bg-yellow-50/50 backdrop-blur-sm border border-yellow-200/50 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-yellow-100 rounded-xl">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-yellow-900 mb-1">
                  Onboarding en attente
                </h3>
                <p className="text-sm text-yellow-800 mb-4">
                  Vous devez compléter l'onboarding Stripe pour recevoir les paiements.
                </p>
                <Button
                  onClick={handleStartOnboarding}
                  disabled={onboarding}
                  icon={onboarding ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                >
                  {onboarding ? 'Redirection...' : 'Compléter l\'onboarding'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Statut - Activé */}
        {status && status.enabled && (
          <div className="bg-teal-50/50 backdrop-blur-sm border border-teal-200/50 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-teal-100 rounded-xl">
                <CheckCircle className="w-5 h-5 text-teal-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-teal-900 mb-1">
                  Stripe Connect activé
                </h3>
                <p className="text-sm text-teal-800 mb-2">
                  Votre compte Stripe Connect est configuré et actif. Vous pouvez recevoir les paiements de vos factures.
                </p>
                <div className="flex flex-col gap-2 text-sm text-teal-700">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    <span>Paiements par carte : {status.chargesEnabled ? 'Activé' : 'Désactivé'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    <span>Virements bancaires : {status.payoutsEnabled ? 'Activé' : 'Désactivé'}</span>
                  </div>
                </div>
                <Button
                  onClick={handleOpenDashboard}
                  variant="secondary"
                  icon={<ExternalLink className="w-4 h-4" />}
                  className="mt-4"
                >
                  Ouvrir le dashboard Stripe
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Commission info */}
        <div className="bg-white/30 backdrop-blur-sm border border-white/40 rounded-2xl p-4">
          <h3 className="font-semibold text-gray-900 mb-2">
            Commission plateforme
          </h3>
          <p className="text-sm text-gray-700">
            Une commission de <span className="font-semibold text-teal-700">2%</span> est prélevée sur chaque paiement reçu.
            Cette commission couvre les frais de la plateforme et du service de paiement.
          </p>
          <p className="text-xs text-gray-600 mt-2">
            Exemple : Pour une facture de 1000€, vous recevrez 980€ (commission de 20€).
          </p>
        </div>
      </div>
    </div>
  );
};
